import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Plus,
  CalendarDays,
  Clock,
  MapPin,
  Users,
  Pencil,
  Lock,
  ArrowLeft,
  X,
  Loader2,
} from "lucide-react";

export default function AdminActivities() {
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [participantSearch, setParticipantSearch] = useState("");
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [historyRegistrations, setHistoryRegistrations] = useState({});
  const [historyRegistrationsLoading, setHistoryRegistrationsLoading] =
    useState({});

  const [form, setForm] = useState({
    title: "",
    description: "",
    activity_date: "",
    activity_time: "",
    location: "",
    registration_deadline: "",
    extra_question_fr: "",
    extra_question_en: "",
    extra_question_ar: "",
  });

  // =========================
  // Vérification ADMIN
  // =========================
  const activeActivity = activities.find((a) => a.is_active);
  const closedActivities = activities.filter((a) => !a.is_active);
  useEffect(() => {
    const token = localStorage.getItem("membreToken");

    if (!token) {
      navigate("/membre/login");
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decoded.exp < currentTime || decoded.role !== "admin") {
        localStorage.removeItem("membreToken");
        localStorage.removeItem("membre");
        navigate("/membre/login");
      }
    } catch {
      localStorage.removeItem("membreToken");
      localStorage.removeItem("membre");
      navigate("/membre/login");
    }
  }, [navigate]);

  // =========================
  // Charger les activités
  // =========================

  async function loadActivities() {
    try {
      setLoading(true);

      const res = await fetch("/api/activity?action=getAll");
      const data = await res.json();

      if (!data.success) {
        toast.error(data.message || "Impossible de récupérer les activités.");
        return;
      }

      setActivities(data.activities || []);
    } catch (error) {
      console.error("LOAD ACTIVITIES ERROR:", error);
      toast.error("Erreur lors du chargement des activités.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadActivities();
  }, []);

  async function loadRegistrations(activityId) {
    if (!activityId) {
      setRegistrations([]);
      return;
    }
    try {
      setRegistrationsLoading(true);
      const res = await fetch(
        `/api/activity?action=getRegistrations&activity_id=${encodeURIComponent(activityId)}`,
      );
      const data = await res.json();
      if (!data.success) {
        toast.error(
          data.message || "Impossible de récupérer les participants.",
        );
        setRegistrations([]);
        return;
      }
      setRegistrations(data.registrations || data.data || []);
    } catch (error) {
      console.error("LOAD REGISTRATIONS ERROR:", error);
      toast.error("Erreur lors du chargement des participants.");
      setRegistrations([]);
    } finally {
      setRegistrationsLoading(false);
    }
  }

  useEffect(() => {
    if (activeActivity?.id) loadRegistrations(activeActivity.id);
    else setRegistrations([]);
  }, [activeActivity?.id]);

  function exportRegistrations() {
    if (!registrations.length) {
      toast.info("Aucun participant à exporter.");
      return;
    }
    const rows = registrations.map((r) => ({
      Nom: r.nom || "",
      Prénom: r.prenom || "",
      Email: r.email || "",
      Téléphone: r.telephone || "",
      "Date de naissance": r.date_naissance || "",
      "Ville de naissance": r.ville_naissance || "",
      "Réponse question": r.extra_question_answer
        ? r.extra_question_answer === "yes"
          ? "Oui"
          : r.extra_question_answer === "no"
            ? "Non"
            : r.extra_question_answer
        : "",
      "Date d'inscription": r.created_at
        ? new Date(r.created_at).toLocaleString("fr-FR")
        : "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Participants");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `participants-${activeActivity?.title || "activite"}.xlsx`,
    );
  }

  const filteredRegistrations = registrations.filter((r) => {
    const q = participantSearch.trim().toLowerCase();
    if (!q) return true;
    return [
      r.nom,
      r.prenom,
      r.email,
      r.telephone,
      r.ville_naissance,
      r.extra_question_answer,
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });

  async function loadHistoryRegistrations(activityId) {
    if (!activityId) return;

    try {
      setHistoryRegistrationsLoading((prev) => ({
        ...prev,
        [activityId]: true,
      }));

      const res = await fetch(
        `/api/activity?action=getRegistrations&activity_id=${encodeURIComponent(activityId)}`,
      );
      const data = await res.json();

      if (!data.success) {
        toast.error(
          data.message || "Impossible de récupérer les participants.",
        );
        return;
      }

      setHistoryRegistrations((prev) => ({
        ...prev,
        [activityId]: data.registrations || data.data || [],
      }));
    } catch (error) {
      console.error("LOAD HISTORY REGISTRATIONS ERROR:", error);
      toast.error("Erreur lors du chargement des participants.");
    } finally {
      setHistoryRegistrationsLoading((prev) => ({
        ...prev,
        [activityId]: false,
      }));
    }
  }

  async function toggleHistoryParticipants(activityId) {
    if (expandedHistoryId === activityId) {
      setExpandedHistoryId(null);
      return;
    }

    setExpandedHistoryId(activityId);

    if (
      !Object.prototype.hasOwnProperty.call(historyRegistrations, activityId)
    ) {
      await loadHistoryRegistrations(activityId);
    }
  }

  function exportHistoryRegistrations(activity) {
    const list = historyRegistrations[activity.id] || [];

    if (!list.length) {
      toast.info("Aucun participant à exporter.");
      return;
    }

    const rows = list.map((r) => ({
      Nom: r.nom || "",
      Prénom: r.prenom || "",
      Email: r.email || "",
      Téléphone: r.telephone || "",
      "Date de naissance": r.date_naissance || "",
      "Ville de naissance": r.ville_naissance || "",
      "Réponse question": r.extra_question_answer
        ? r.extra_question_answer === "yes"
          ? "Oui"
          : r.extra_question_answer === "no"
            ? "Non"
            : r.extra_question_answer
        : "",
      "Date d'inscription": r.created_at
        ? new Date(r.created_at).toLocaleString("fr-FR")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Participants");

    const buffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `participants-${activity.title || "activite"}.xlsx`,
    );
  }

  // =========================
  // Form change
  // =========================

  function handleChange(e) {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  // =========================
  // Créer activité
  // =========================

  async function handleCreate(e) {
    e.preventDefault();

    if (saving) return;

    setSaving(true);

    try {
      const res = await fetch("/api/activity?action=create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          registration_deadline: new Date(
            form.registration_deadline,
          ).toISOString(),
        }),
      });

      const data = await res.json();

      if (!data.success) {
        toast.error(data.message || "Impossible de créer l'activité.");
        return;
      }

      toast.success("Activité lancée avec succès !");

      setForm({
        title: "",
        description: "",
        activity_date: "",
        activity_time: "",
        location: "",
        registration_deadline: "",
        extra_question_fr: "",
        extra_question_en: "",
        extra_question_ar: "",
      });

      setShowForm(false);

      await loadActivities();
    } catch (error) {
      console.error("CREATE ACTIVITY ERROR:", error);
      toast.error("Erreur serveur.");
    } finally {
      setSaving(false);
    }
  }

  // =========================
  // Clôturer
  // =========================

  async function handleClose(id) {
    const confirmed = window.confirm(
      "Voulez-vous vraiment clôturer cette activité ?",
    );

    if (!confirmed) return;

    try {
      const res = await fetch("/api/activity?action=close", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });

      const data = await res.json();

      if (!data.success) {
        toast.error(data.message || "Impossible de clôturer.");
        return;
      }

      toast.success("Activité clôturée.");

      await loadActivities();
    } catch (error) {
      console.error("CLOSE ACTIVITY ERROR:", error);
      toast.error("Erreur serveur.");
    }
  }

  // =========================
  // Modifier
  // =========================

  function handleEdit(activity) {
    navigate(`/admin/activities/edit/${activity.id}`);
  }

  // =========================
  // Date format
  // =========================

  function formatDate(date) {
    if (!date) return "-";

    return new Date(`${date}T00:00:00`).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }

  function formatTime(time) {
    if (!time) return "-";

    return time.slice(0, 5);
  }

  function formatDeadline(deadline) {
    if (!deadline) return "-";

    return new Date(deadline).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-slate-100 dark:bg-slate-950 p-4 sm:p-6 lg:p-8">
      {/* =========================
          HEADER
      ========================= */}

      <div className="w-full max-w-7xl mx-auto min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate("/admin/welcome")}
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 transition mb-3"
            >
              <ArrowLeft size={17} />
              Retour au dashboard
            </button>

            <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white">
              Gestion des activités
            </h1>

            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Créez, gérez et suivez les activités de l'association.
            </p>
          </div>

          {!activeActivity && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg transition"
            >
              <Plus size={19} />
              Lancer une activité
            </button>
          )}
        </div>

        {/* =========================
            ACTIVE ACTIVITY
        ========================= */}

        {loading ? (
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm p-12 flex justify-center">
            <Loader2 size={32} className="animate-spin text-indigo-600" />
          </div>
        ) : activeActivity ? (
          <>
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl overflow-hidden border border-slate-200 dark:border-slate-800">
              {/* TOP */}

              <div className="bg-gradient-to-r from-indigo-600 to-violet-700 p-6 sm:p-8 text-white">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5 min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold mb-4">
                      <span className="w-2 h-2 rounded-full bg-green-400" />
                      Activité active
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-extrabold break-words whitespace-normal">
                      {activeActivity.title}
                    </h2>

                    <p className="text-white/80 mt-2 max-w-3xl break-words whitespace-normal leading-relaxed">
                      {activeActivity.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* DETAILS */}

              <div className="p-6 sm:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <InfoCard
                    icon={<CalendarDays size={20} />}
                    label="Date"
                    value={formatDate(activeActivity.activity_date)}
                  />

                  <InfoCard
                    icon={<Clock size={20} />}
                    label="Heure"
                    value={formatTime(activeActivity.activity_time)}
                  />

                  <InfoCard
                    icon={<MapPin size={20} />}
                    label="Lieu"
                    value={activeActivity.location}
                  />

                  <InfoCard
                    icon={<Clock size={20} />}
                    label="Date limite"
                    value={formatDeadline(activeActivity.registration_deadline)}
                  />
                </div>
                {/* ACTIONS */}

                <div className="flex flex-col sm:flex-row gap-3 mt-7 pt-6 border-t border-slate-100 dark:border-slate-800">
                  <button
                    onClick={() => handleEdit(activeActivity)}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold transition"
                  >
                    <Pencil size={17} />
                    Modifier
                  </button>

                  <button
                    onClick={() => handleClose(activeActivity.id)}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold transition"
                  >
                    <Lock size={17} />
                    Clôturer
                  </button>
                </div>
              </div>
            </div>

            {/* =========================
              PARTICIPANTS
          ========================= */}

            <section className="mt-6 w-full min-w-0 bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <div className="p-5 sm:p-6 border-b border-slate-100 dark:border-slate-800 min-w-0">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 flex items-center justify-center">
                      <Users size={20} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                        Participants
                      </h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {registrations.length}{" "}
                        {registrations.length === 1
                          ? "participant inscrit"
                          : "participants inscrits"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                    <input
                      type="search"
                      value={participantSearch}
                      onChange={(e) => setParticipantSearch(e.target.value)}
                      placeholder="Rechercher un participant..."
                      className="w-full sm:w-72 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={exportRegistrations}
                      disabled={!registrations.length}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold transition"
                    >
                      📥 Télécharger Excel
                    </button>
                  </div>
                </div>
              </div>

              {registrationsLoading ? (
                <div className="p-12 flex justify-center">
                  <Loader2 size={28} className="animate-spin text-indigo-600" />
                </div>
              ) : filteredRegistrations.length === 0 ? (
                <div className="p-10 sm:p-14 text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mb-4">
                    <Users size={26} />
                  </div>
                  <h3 className="font-bold text-slate-800 dark:text-white">
                    {participantSearch
                      ? "Aucun participant trouvé"
                      : "Aucune inscription pour le moment"}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                    {participantSearch
                      ? "Essayez avec un autre nom, email ou numéro de téléphone."
                      : "Les participants apparaîtront ici dès qu'une personne s'inscrira à cette activité."}
                  </p>
                </div>
              ) : (
                <>
                  <div className="hidden md:block w-full max-w-full overflow-x-auto">
                    <table className="w-full min-w-[900px] text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-800/70">
                        <tr className="text-left text-slate-500 dark:text-slate-400">
                          <th className="px-5 py-4 font-semibold">Photo</th>
                          <th className="px-5 py-4 font-semibold">Nom</th>
                          <th className="px-5 py-4 font-semibold">Prénom</th>
                          <th className="px-5 py-4 font-semibold">Email</th>
                          <th className="px-5 py-4 font-semibold">Téléphone</th>
                          <th className="px-5 py-4 font-semibold">Naissance</th>
                          <th className="px-5 py-4 font-semibold">Ville</th>
                          <th className="px-5 py-4 font-semibold">Réponse</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {filteredRegistrations.map((participant) => (
                          <tr
                            key={participant.id}
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                          >
                            <td className="px-5 py-4">
                              <ParticipantAvatar participant={participant} />
                            </td>
                            <td className="px-5 py-4 font-semibold text-slate-800 dark:text-white">
                              {participant.nom || "-"}
                            </td>
                            <td className="px-5 py-4 text-slate-700 dark:text-slate-300">
                              {participant.prenom || "-"}
                            </td>
                            <td className="px-5 py-4 text-slate-600 dark:text-slate-300 break-all">
                              {participant.email || "-"}
                            </td>
                            <td className="px-5 py-4 text-slate-600 dark:text-slate-300 break-words">
                              {participant.telephone || "-"}
                            </td>
                            <td className="px-5 py-4 text-slate-600 dark:text-slate-300">
                              {participant.date_naissance || "-"}
                            </td>
                            <td className="px-5 py-4 text-slate-600 dark:text-slate-300 break-words">
                              {participant.ville_naissance || "-"}
                            </td>
                            <td className="px-5 py-4">
                              {participant.extra_question_answer === "yes" ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold">
                                  Oui
                                </span>
                              ) : participant.extra_question_answer === "no" ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold">
                                  Non
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="md:hidden p-4 space-y-3">
                    {filteredRegistrations.map((participant) => (
                      <div
                        key={participant.id}
                        className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-4"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <ParticipantAvatar
                            participant={participant}
                            size="large"
                          />
                          <div className="min-w-0">
                            <h3 className="font-bold text-slate-900 dark:text-white truncate">
                              {participant.prenom || ""} {participant.nom || ""}
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                              {participant.email || "-"}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-2 text-sm">
                          <ParticipantDetail
                            label="Téléphone"
                            value={participant.telephone}
                          />
                          <ParticipantDetail
                            label="Date de naissance"
                            value={participant.date_naissance}
                          />
                          <ParticipantDetail
                            label="Ville de naissance"
                            value={participant.ville_naissance}
                          />
                          <ParticipantDetail
                            label="Réponse à la question"
                            value={
                              participant.extra_question_answer === "yes"
                                ? "Oui"
                                : participant.extra_question_answer === "no"
                                  ? "Non"
                                  : "-"
                            }
                          />
                          <ParticipantDetail
                            label="Inscrit le"
                            value={
                              participant.created_at
                                ? new Date(
                                    participant.created_at,
                                  ).toLocaleString("fr-FR")
                                : "-"
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>
          </>
        ) : (
          /* =========================
             NO ACTIVE ACTIVITY
          ========================= */

          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-dashed border-slate-300 dark:border-slate-700 p-10 sm:p-16 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 mb-5">
              <CalendarDays size={30} />
            </div>

            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              Aucune activité active
            </h2>

            <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-md mx-auto">
              Lancez une nouvelle activité pour permettre aux visiteurs de
              s'inscrire.
            </p>

            <button
              onClick={() => setShowForm(true)}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg transition"
            >
              <Plus size={19} />
              Lancer une activité
            </button>
          </div>
        )}

        {/* =========================
            HISTORIQUE
        ========================= */}

        {closedActivities.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Historique
                </h2>

                <p className="text-sm text-slate-500">
                  Activités précédemment clôturées.
                </p>
              </div>

              <span className="px-3 py-1 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-semibold">
                {closedActivities.length}
              </span>
            </div>

            <div className="space-y-3">
              {closedActivities.map((activity) => (
                <React.Fragment key={activity.id}>
                  <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-800 dark:text-white">
                          {activity.title}
                        </h3>

                        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">
                          Clôturée
                        </span>
                      </div>

                      <p className="text-sm text-slate-500 mt-1">
                        {formatDate(activity.activity_date)} ·{" "}
                        {activity.location}
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => toggleHistoryParticipants(activity.id)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition"
                      >
                        <Users size={15} />
                        {expandedHistoryId === activity.id
                          ? "Masquer les participants"
                          : "Voir les participants"}
                      </button>

                      <button
                        onClick={() => exportHistoryRegistrations(activity)}
                        disabled={
                          !historyRegistrations[activity.id]?.length ||
                          historyRegistrationsLoading[activity.id]
                        }
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition"
                      >
                        📥 Excel
                      </button>
                    </div>
                  </div>

                  {expandedHistoryId === activity.id && (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                      {historyRegistrationsLoading[activity.id] ? (
                        <div className="py-8 flex justify-center">
                          <Loader2
                            size={24}
                            className="animate-spin text-indigo-600"
                          />
                        </div>
                      ) : (historyRegistrations[activity.id] || []).length ===
                        0 ? (
                        <div className="py-8 text-center">
                          <Users
                            size={26}
                            className="mx-auto mb-2 text-slate-400"
                          />
                          <p className="font-semibold text-slate-700 dark:text-white">
                            Aucun participant
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            Cette activité ne contient aucune inscription.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="hidden md:block w-full max-w-full overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-700">
                            <table className="w-full min-w-[900px] text-sm">
                              <thead className="bg-slate-50 dark:bg-slate-800/70">
                                <tr className="text-left text-slate-500 dark:text-slate-400">
                                  <th className="px-4 py-3 font-semibold">
                                    Photo
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Nom
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Prénom
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Email
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Téléphone
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Naissance
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Ville
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {(historyRegistrations[activity.id] || []).map(
                                  (participant) => (
                                    <tr
                                      key={participant.id}
                                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                                    >
                                      <td className="px-4 py-3">
                                        <ParticipantAvatar
                                          participant={participant}
                                        />
                                      </td>
                                      <td className="px-4 py-3 font-semibold text-slate-800 dark:text-white">
                                        {participant.nom || "-"}
                                      </td>
                                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                        {participant.prenom || "-"}
                                      </td>
                                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                        {participant.email || "-"}
                                      </td>
                                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                        {participant.telephone || "-"}
                                      </td>
                                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                        {participant.date_naissance || "-"}
                                      </td>
                                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                        {participant.ville_naissance || "-"}
                                      </td>
                                      <td className="px-4 py-3">
                                        {participant.extra_question_answer ===
                                        "yes" ? (
                                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold">
                                            Oui
                                          </span>
                                        ) : participant.extra_question_answer ===
                                          "no" ? (
                                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold">
                                            Non
                                          </span>
                                        ) : (
                                          <span className="text-slate-400">
                                            -
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ),
                                )}
                              </tbody>
                            </table>
                          </div>

                          <div className="md:hidden space-y-3">
                            {(historyRegistrations[activity.id] || []).map(
                              (participant) => (
                                <div
                                  key={participant.id}
                                  className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-4"
                                >
                                  <div className="flex items-center gap-3 mb-4">
                                    <ParticipantAvatar
                                      participant={participant}
                                      size="large"
                                    />
                                    <div className="min-w-0">
                                      <h3 className="font-bold text-slate-900 dark:text-white truncate">
                                        {participant.prenom || ""}{" "}
                                        {participant.nom || ""}
                                      </h3>
                                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                        {participant.email || "-"}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 gap-2 text-sm">
                                    <ParticipantDetail
                                      label="Téléphone"
                                      value={participant.telephone}
                                    />
                                    <ParticipantDetail
                                      label="Date de naissance"
                                      value={participant.date_naissance}
                                    />
                                    <ParticipantDetail
                                      label="Ville de naissance"
                                      value={participant.ville_naissance}
                                    />
                                    <ParticipantDetail
                                      label="Réponse à la question"
                                      value={
                                        participant.extra_question_answer ===
                                        "yes"
                                          ? "Oui"
                                          : participant.extra_question_answer ===
                                              "no"
                                            ? "Non"
                                            : "-"
                                      }
                                    />
                                    <ParticipantDetail
                                      label="Inscrit le"
                                      value={
                                        participant.created_at
                                          ? new Date(
                                              participant.created_at,
                                            ).toLocaleString("fr-FR")
                                          : "-"
                                      }
                                    />
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* =========================
          MODAL CREATE
      ========================= */}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* MODAL HEADER */}

            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Lancer une nouvelle activité
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  Remplissez les informations de l'activité.
                </p>
              </div>

              <button
                onClick={() => setShowForm(false)}
                className="w-9 h-9 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-500"
              >
                <X size={20} />
              </button>
            </div>

            {/* FORM */}

            <form onSubmit={handleCreate} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Titre *
                </label>

                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Ex : Atelier Leadership"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Description *
                </label>

                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Décrivez l'activité..."
                  rows={4}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Date *
                  </label>

                  <input
                    type="date"
                    name="activity_date"
                    value={form.activity_date}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Heure *
                  </label>

                  <input
                    type="time"
                    name="activity_time"
                    value={form.activity_time}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Lieu *
                </label>

                <input
                  type="text"
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  placeholder="Ex : Rabat"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Date limite d'inscription *
                </label>

                <input
                  type="datetime-local"
                  name="registration_deadline"
                  value={form.registration_deadline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />

                <p className="text-xs text-slate-400 mt-2">
                  La date limite doit être avant la date de l'activité.
                </p>
              </div>

              {/* QUESTION SUPPLÉMENTAIRE — OPTIONNEL */}
              <div className="pt-2">
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                    Question supplémentaire
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Optionnelle. Si vous la remplissez, elle apparaîtra dans le
                    formulaire d'inscription avec une réponse Oui / Non.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      🇫🇷 Français
                    </label>
                    <input
                      type="text"
                      name="extra_question_fr"
                      value={form.extra_question_fr}
                      onChange={handleChange}
                      placeholder="Ex : Avez-vous déjà participé à une activité ALEOA ?"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      🇬🇧 English
                    </label>
                    <input
                      type="text"
                      name="extra_question_en"
                      value={form.extra_question_en}
                      onChange={handleChange}
                      placeholder="Ex : Have you already participated in an ALEOA activity?"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      🇲🇦 العربية
                    </label>
                    <input
                      type="text"
                      name="extra_question_ar"
                      value={form.extra_question_ar}
                      onChange={handleChange}
                      dir="rtl"
                      placeholder="مثال: هل سبق لك المشاركة في نشاط من أنشطة ALEOA؟"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* BUTTONS */}

              <div className="flex flex-col sm:flex-row gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-white font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-semibold flex items-center justify-center gap-2 transition"
                >
                  {saving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Création...
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Lancer l'activité
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ParticipantAvatar({ participant, size = "normal" }) {
  const large = size === "large";

  const [imageError, setImageError] = useState(false);

  const imageSize = large ? "w-14 h-14" : "w-10 h-10";

  if (!participant.photo_url || imageError) {
    return (
      <div
        className={`${imageSize} rounded-xl bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 flex items-center justify-center font-bold border border-slate-200 dark:border-slate-700`}
      >
        {(participant.prenom || participant.nom || "?").charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <img
      src={participant.photo_url}
      alt={`${participant.prenom || ""} ${participant.nom || ""}`}
      className={`${imageSize} rounded-xl object-cover border border-slate-200 dark:border-slate-700`}
      onError={(e) => {
        console.error("PHOTO LOAD ERROR:", participant.photo_url);
        setImageError(true);
      }}
    />
  );
}
function ParticipantDetail({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-200/70 dark:border-slate-700 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <span className="text-sm font-medium text-slate-700 dark:text-slate-200 text-right break-all">
        {value || "-"}
      </span>
    </div>
  );
}

// =========================
// INFO CARD
// =========================

function InfoCard({ icon, label, value }) {
  return (
    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/70 border border-slate-100 dark:border-slate-700">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 flex items-center justify-center shrink-0">
          {icon}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">
            {label}
          </p>

          <p className="text-sm font-bold text-slate-800 dark:text-white break-words whitespace-normal">
            {value || "-"}
          </p>
        </div>
      </div>
    </div>
  );
}
